// ============================================================
// HandoutFolders - the cards in the handouts drawer, grouped by
// folder. Split from HandoutsDrawer (which keeps the tools, the
// selection and the error line); a card is its thumbnail, title,
// audience and size, and - for the GM - Show, Edit and an arming Delete.
// ============================================================
import { useState } from 'react';
import common from '../styles/common.module.css';
import styles from '../styles/HandoutsDrawer.module.css';
import clsx from '../utils/clsx';
import { useHandoutsContext, type HandoutsContextValue } from '../contexts/HandoutsContext';
import { audienceLabel, formatBytes, type folderTree, type Handout } from '../engine/handouts';
import useFileUrl from '../hooks/useFileUrl';
import { keptFileUrl } from '../utils/keptFiles';
import { warnIfRejected } from '../diagnostics/log';

const ICON: Record<Handout['kind'], string> = { image: '🖼', pdf: '📄', note: '📝', link: '🔗' };

function Thumb({ h, onOpen }: { h: Handout; onOpen: () => void }) {
  const ctx = useHandoutsContext()!;
  // Kept once per page (keptFileUrl): a renewed token was a new URL, and a new download, on every mount.
  const { url } = useFileUrl(h.thumb, (ref) => keptFileUrl(ref, (r) => ctx.fileUrl({ ...h, thumb: r }, 'thumb')));
  return (
    <button
      type="button"
      className={styles.thumb}
      onClick={onOpen}
      aria-label={`Open ${h.title || h.kind}`}
      data-testid="handout-open"
    >
      {url ? <img src={url} alt="" /> : <span className={styles.icon}>{ICON[h.kind]}</span>}
    </button>
  );
}

interface CardProps {
  h: Handout;
  onEdit: () => void;
  onReveal: () => void;
  /** Select mode (v8.363.11): a checkbox instead of the per-card buttons. */
  picked: boolean | null;
  onPick: (on: boolean) => void;
}

/** A card's audience, carriage, size and tags. */
/** The GM's: how many of those a shown handout went to have opened it (v9.8.0). Nothing on a draft. */
function SeenBadge({ h }: { h: Handout }) {
  const ctx = useHandoutsContext()!;
  const { seen, of } = ctx.seenOf(h);
  if (of === 0) return null;
  return (
    <span className={styles.badge} data-testid="handout-seen" data-all={seen === of || undefined}>
      seen by {seen} of {of}
    </span>
  );
}

function CardMeta({ h }: { h: Handout }) {
  const ctx = useHandoutsContext()!;
  const notCarried = (h.kind === 'image' || h.kind === 'pdf') && !h.file;
  return (
    <div className={styles.meta}>
      {ctx.isGm && (
        <span className={clsx(styles.badge, h.audience.length === 0 && styles.draft)} data-testid="handout-audience">
          {audienceLabel(h.audience, ctx.people)}
        </span>
      )}
      {ctx.isGm && <SeenBadge h={h} />}
      {ctx.unread.has(h.id) && (
        <span className={styles.newPip} data-testid="handout-new">
          new
        </span>
      )}
      {notCarried && <span className={styles.badge}>not carried</span>}
      {h.size > 0 && <span className={styles.size}>{formatBytes(h.size)}</span>}
      {h.tags.map((t) => (
        <span key={t} className={styles.tag}>
          {t}
        </span>
      ))}
    </div>
  );
}

/**
 * Present (v9.8.0): open this handout on every screen in its audience. A
 * draft has nobody to present to, so its button opens the reveal dialog,
 * whose "Show and present" does both. While it is up, the button ends it.
 */
function PresentButton({ h, onReveal }: Pick<CardProps, 'h' | 'onReveal'>) {
  const ctx = useHandoutsContext()!;
  if (ctx.presenting === h.id) {
    return (
      <button
        type="button"
        className={common.btnSmall}
        title="Close it on every screen"
        onClick={() => warnIfRejected(ctx.endPresent(h.id), 'handouts', 'end present')}
        data-testid="handout-present-end"
      >
        Stop showing
      </button>
    );
  }
  const draft = h.audience.length === 0;
  return (
    <button
      type="button"
      className={common.btnSmall}
      title={
        draft ? 'Choose who sees it, then open it on their screens' : 'Open it on the screen of everyone who can see it'
      }
      onClick={() => (draft ? onReveal() : warnIfRejected(ctx.present(h.id), 'handouts', 'present'))}
      data-testid="handout-present"
    >
      Present
    </button>
  );
}

/** The GM's per-card buttons. Delete arms first; the second press removes for good. */
function CardActions({ h, onEdit, onReveal }: Pick<CardProps, 'h' | 'onEdit' | 'onReveal'>) {
  const ctx = useHandoutsContext()!;
  const [arming, setArming] = useState(false);
  return (
    <div className={styles.actions}>
      <button type="button" className={common.btnSmall} onClick={onReveal} data-testid="handout-reveal">
        Show…
      </button>
      <PresentButton h={h} onReveal={onReveal} />
      {(h.kind === 'note' || h.kind === 'link') && (
        <button type="button" className={common.btnSmall} onClick={onEdit} data-testid="handout-edit">
          Edit
        </button>
      )}
      {arming ? (
        <button
          type="button"
          className={clsx(common.btnSmall, styles.danger)}
          onClick={() => warnIfRejected(ctx.remove(h.id), 'handouts', 'delete')}
          data-testid="handout-delete-confirm"
        >
          Delete for good
        </button>
      ) : (
        <button type="button" className={common.btnSmall} onClick={() => setArming(true)} data-testid="handout-delete">
          Delete
        </button>
      )}
    </div>
  );
}

function Card({ h, onEdit, onReveal, picked, onPick }: CardProps) {
  const ctx = useHandoutsContext()!;
  return (
    <div
      className={clsx(styles.card, picked && styles.picked)}
      data-testid="handout-card"
      data-id={h.id}
      data-kind={h.kind}
      data-unread={ctx.unread.has(h.id) || undefined}
    >
      {picked !== null && (
        <input
          type="checkbox"
          className={styles.pick}
          checked={picked}
          onChange={(e) => onPick(e.target.checked)}
          aria-label={`Select ${h.title || h.kind}`}
          data-testid="handout-pick"
        />
      )}
      <Thumb h={h} onOpen={() => ctx.view(h.id)} />
      <div className={styles.cardBody}>
        <button type="button" className={styles.title} onClick={() => ctx.view(h.id)} data-testid="handout-title">
          {h.title || h.kind}
        </button>
        <CardMeta h={h} />
        {ctx.isGm && picked === null && <CardActions h={h} onEdit={onEdit} onReveal={onReveal} />}
      </div>
    </div>
  );
}

interface HandoutFoldersProps {
  ctx: HandoutsContextValue;
  folders: ReturnType<typeof folderTree>;
  picked: Set<string> | null;
  onEdit: (h: Handout) => void;
  onReveal: (id: string) => void;
  onPick: (id: string, on: boolean) => void;
}

type Folder = ReturnType<typeof folderTree>[number];

/** One folder's heading (named, or "Unfiled" when there are several) and its cards. */
function FolderSection({
  folder,
  titled,
  cardProps,
}: {
  folder: Folder;
  titled: boolean;
  cardProps: Omit<HandoutFoldersProps, 'ctx' | 'folders'>;
}) {
  const { picked, onEdit, onReveal, onPick } = cardProps;
  return (
    <section className={styles.folder} data-testid="handout-folder-section" data-folder={folder.name}>
      {titled && <h4 className={styles.folderName}>{folder.name || 'Unfiled'}</h4>}
      {folder.handouts.map((h) => (
        <Card
          key={h.id}
          h={h}
          onEdit={() => onEdit(h)}
          onReveal={() => onReveal(h.id)}
          picked={picked ? picked.has(h.id) : null}
          onPick={(on) => onPick(h.id, on)}
        />
      ))}
    </section>
  );
}

/**
 * The drawer's folders of cards, or the empty-state sentence. `picked`
 * is null outside select mode; inside it each card shows a checkbox
 * instead of its buttons.
 */
export default function HandoutFolders({ ctx, folders, ...cardProps }: HandoutFoldersProps) {
  return (
    <div className={styles.list}>
      {ctx.handouts.length === 0 && (
        <div className={common.overviewEmpty} data-testid="handouts-empty">
          {ctx.isGm ? 'Nothing yet. Add an image, a PDF, a note or a link.' : 'Nothing has been shown to you yet.'}
        </div>
      )}
      {folders.map((f) => (
        <FolderSection key={f.name} folder={f} titled={!!f.name || folders.length > 1} cardProps={cardProps} />
      ))}
    </div>
  );
}
